import React, { useEffect, useState } from 'react';
import {
    TextField,
    Button,
    Grid,
    Typography,
    Container,
    Box,
    InputAdornment,
    IconButton,
    Link,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import VipFormerLogo from '../assets/images/logo.png';
import axiosInstance from '../Services/axiosInstance.js';
import { getErrorMessage, handleSpaces } from '../utils/helper';
import { success, danger } from '../utils/message-box';

const PasswordField = ({
    label,
    value,
    onChange,
    error,
    showPassword,
    onToggleShow,
}) => (
    <>
        <Typography className="gray8 font-weight-500 f-14">{label}</Typography>
        <TextField
            required
            fullWidth
            type={showPassword ? 'text' : 'password'}
            className={`form-control sign-field input-group mb-0 inputcontrasts${error ? ' is-invalid' : ''}`}
            margin="normal"
            placeholder={`Enter ${label.toLowerCase()}`}
            onKeyPress={handleSpaces}
            value={value}
            onChange={onChange}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            aria-label={`show-hide-${label.toLowerCase().replace(/\s+/g, '-')}`}
                            onClick={onToggleShow}
                            onMouseDown={(event) => event.preventDefault()}
                            sx={{ '& .MuiTouchRipple-root': { position: 'static' } }}
                            tabIndex={0}
                        >
                            {showPassword ? <Visibility tabIndex={-1} /> : <VisibilityOffIcon tabIndex={-1} />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
        <span className="danger-color error-msg">{error || ''}</span>
    </>
);

const ResetPassword = () => {
    const navigate = useNavigate();
    const isAuthenticated = !!localStorage.getItem('access-token');

    const [email, setEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [emailError, setEmailError] = useState('');
    const [currentPasswordError, setCurrentPasswordError] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const storedEmail = localStorage.getItem('email');
        if (storedEmail) {
            setEmail(storedEmail);
        }
    }, []);

    const validateForm = () => {
        const errors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!email.trim()) {
            errors.email = 'Email is required';
        } else if (!emailRegex.test(email.trim())) {
            errors.email = 'Please enter a valid email address';
        }

        if (!currentPassword) {
            errors.currentPassword = 'Current password is required';
        } else if (currentPassword.length > 128) {
            errors.currentPassword = 'Exceeded maximum character length of 128';
        }

        if (!newPassword) {
            errors.newPassword = 'New password is required';
        } else if (newPassword.length < 6) {
            errors.newPassword = 'Password must be at least 6 characters';
        } else if (newPassword.length > 128) {
            errors.newPassword = 'Exceeded maximum character length of 128';
        }

        if (!confirmPassword) {
            errors.confirmPassword = 'Please confirm your new password';
        } else if (confirmPassword !== newPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setEmailError(errors.email || '');
        setCurrentPasswordError(errors.currentPassword || '');
        setNewPasswordError(errors.newPassword || '');
        setConfirmPasswordError(errors.confirmPassword || '');

        return Object.keys(errors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (loading || !validateForm()) {
            if (!loading) {
                danger('Please fix the errors before submitting.');
            }
            return false;
        }

        setLoading(true);
        axiosInstance.post('/api/v1/auth/resetpassword', {
            email: email.trim(),
            current_password: currentPassword,
            new_password: newPassword,
        })
            .then(() => {
                success('Password reset successfully. Please sign in with your new password.');
                localStorage.clear();
                navigate('/signin');
            })
            .catch((error) => {
                danger(getErrorMessage(error, 'Failed to reset password.'));
            })
            .finally(() => {
                setLoading(false);
            });

        return false;
    };

    return (
        <Container component="main" maxWidth="xs">
            {!isAuthenticated && (
                <Box className="d-flex justify-end mt-10">
                    <Link
                        component="button"
                        className="link-color f-16 font-weight-500 mr-10 download-hover"
                        onClick={() => navigate('/signin')}
                        sx={{ textDecoration: 'none' }}
                    >
                        Back to Sign In
                    </Link>
                </Box>
            )}
            <Box
                sx={{
                    marginTop: isAuthenticated ? 2 : 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {!isAuthenticated && (
                    <Grid className="ml-40 text-center">
                        <Typography>
                            <img src={VipFormerLogo} style={{ width: '50%' }} alt="VIP Farmer logo" />
                        </Typography>
                    </Grid>
                )}
                <Typography className="gray9 f-24 font-weight-600 mt-20">Reset Password</Typography>
                <Typography className="gray7 f-16 font-weight-400 mt-10 text-center">
                    Enter your email, current password, and choose a new password.
                </Typography>

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3, width: '100%' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} className="login-padding">
                            <Typography className="gray8 font-weight-500 f-14">Email</Typography>
                            <TextField
                                required
                                fullWidth
                                type="email"
                                className={`form-control input-group mb-0 inputcontrasts${emailError ? ' is-invalid' : ''}`}
                                margin="normal"
                                placeholder="Enter email"
                                value={email}
                                disabled={isAuthenticated}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailError('');
                                }}
                            />
                            <span className="danger-color error-msg">{emailError || ''}</span>
                        </Grid>
                        <Grid item xs={12} className="login-padding">
                            <PasswordField
                                label="Current Password"
                                value={currentPassword}
                                error={currentPasswordError}
                                showPassword={showCurrentPassword}
                                onToggleShow={() => setShowCurrentPassword((prev) => !prev)}
                                onChange={(e) => {
                                    setCurrentPassword(e.target.value);
                                    setCurrentPasswordError('');
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} className="login-padding">
                            <PasswordField
                                label="New Password"
                                value={newPassword}
                                error={newPasswordError}
                                showPassword={showNewPassword}
                                onToggleShow={() => setShowNewPassword((prev) => !prev)}
                                onChange={(e) => {
                                    setNewPassword(e.target.value);
                                    setNewPasswordError('');
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} className="login-padding">
                            <PasswordField
                                label="Confirm New Password"
                                value={confirmPassword}
                                error={confirmPasswordError}
                                showPassword={showConfirmPassword}
                                onToggleShow={() => setShowConfirmPassword((prev) => !prev)}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    setConfirmPasswordError('');
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        className="btn primary-button login-button"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={loading}
                    >
                        {loading ? <span className="loader-login"></span> : 'Reset Password'}
                    </Button>
                    {isAuthenticated && (
                        <Grid container justifyContent="center">
                            <Grid item>
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => navigate('/home')}
                                    className="link-color f-14 font-weight-500"
                                    sx={{ textDecoration: 'none' }}
                                >
                                    Cancel
                                </Link>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Box>
        </Container>
    );
};

export default ResetPassword;
